import { type ProjectEntry, type ModelSlug, type ProviderKind } from "@t3tools/contracts";
import { memo } from "react";
import { type ComposerSlashCommand, type ComposerTriggerKind } from "../../composer-logic";
import { BotIcon, SparklesIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Badge } from "../ui/badge";
import { Command, CommandItem, CommandList } from "../ui/command";
import { VscodeEntryIcon } from "./VscodeEntryIcon";

export type ComposerCommandItem =
  | {
      id: string;
      type: "path";
      path: string;
      pathKind: ProjectEntry["kind"];
      label: string;
      description: string;
    }
  | {
      id: string;
      type: "slash-command";
      command: ComposerSlashCommand;
      label: string;
      description: string;
    }
  | {
      id: string;
      type: "skill";
      name: string;
      status: "active" | "disabled" | "archived";
      availableInSession: boolean;
      label: string;
      description: string;
    }
  | {
      id: string;
      type: "model";
      provider: ProviderKind;
      model: ModelSlug;
      label: string;
      description: string;
    };

export const ComposerCommandMenu = memo(function ComposerCommandMenu(props: {
  items: ComposerCommandItem[];
  resolvedTheme: "light" | "dark";
  isLoading: boolean;
  triggerKind: ComposerTriggerKind | null;
  activeItemId: string | null;
  onHighlightedItemChange: (itemId: string | null) => void;
  onSelect: (item: ComposerCommandItem) => void;
}) {
  return (
    <Command
      mode="none"
      onItemHighlighted={(highlightedValue) => {
        props.onHighlightedItemChange(
          typeof highlightedValue === "string" ? highlightedValue : null,
        );
      }}
    >
      <div className="relative overflow-hidden rounded-xl border border-border/80 bg-popover/96 shadow-lg/8 backdrop-blur-xs">
        <CommandList className="max-h-64">
          {props.items.map((item) => (
            <ComposerCommandMenuItem
              key={item.id}
              item={item}
              resolvedTheme={props.resolvedTheme}
              isActive={props.activeItemId === item.id}
              onSelect={props.onSelect}
            />
          ))}
        </CommandList>
        {props.items.length === 0 && (
          <p className="px-3 py-2 text-muted-foreground/70 text-xs">
            {props.isLoading
              ? props.triggerKind === "path"
                ? "Searching workspace files..."
                : props.triggerKind === "slash-command" || props.triggerKind === "skill"
                  ? "Loading skills..."
                  : "Loading models..."
              : props.triggerKind === "path"
                ? "No matching files or folders."
                : props.triggerKind === "skill"
                  ? "No matching skills."
                  : "No matching command."}
          </p>
        )}
      </div>
    </Command>
  );
});

const ComposerCommandMenuItem = memo(function ComposerCommandMenuItem(props: {
  item: ComposerCommandItem;
  resolvedTheme: "light" | "dark";
  isActive: boolean;
  onSelect: (item: ComposerCommandItem) => void;
}) {
  return (
    <CommandItem
      value={props.item.id}
      disabled={props.item.type === "skill" && !props.item.availableInSession}
      className={cn(
        "cursor-pointer select-none gap-2",
        props.item.type === "skill" && "grid grid-cols-[1rem_minmax(0,1fr)] gap-y-0.5",
        props.isActive && "bg-accent text-accent-foreground",
        props.item.type === "skill" &&
          !props.item.availableInSession &&
          "cursor-not-allowed opacity-60",
      )}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={() => {
        if (props.item.type !== "skill" || props.item.availableInSession) {
          props.onSelect(props.item);
        }
      }}
    >
      {props.item.type === "path" ? (
        <VscodeEntryIcon
          pathValue={props.item.path}
          kind={props.item.pathKind}
          theme={props.resolvedTheme}
        />
      ) : null}
      {props.item.type === "slash-command" ? (
        <BotIcon className="size-4 text-muted-foreground/80" />
      ) : null}
      {props.item.type === "skill" ? (
        <SparklesIcon className="size-4 text-muted-foreground/80" />
      ) : null}
      {props.item.type === "model" ? (
        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
          model
        </Badge>
      ) : null}
      <span
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          props.item.type === "skill" ? "flex-wrap" : "truncate",
        )}
      >
        <span
          className={
            props.item.type === "skill"
              ? "min-w-0 whitespace-normal [overflow-wrap:anywhere]"
              : "truncate"
          }
        >
          {props.item.label}
        </span>
        {props.item.type === "skill" && (
          <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px]">
            {props.item.status === "disabled"
              ? "disabled"
              : props.item.availableInSession
                ? "skill"
                : "refresh to use"}
          </Badge>
        )}
      </span>
      <span
        className={cn(
          "truncate text-muted-foreground/70 text-xs",
          props.item.type === "skill" && "col-start-2",
        )}
      >
        {props.item.description}
      </span>
    </CommandItem>
  );
});
